import React, { useEffect, useState } from 'react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { Client, PersonaKycStatus } from '@/types/client';
import { Modal } from '@/types/modal';
import Grid, { GridAction, itemHtml, itemList, itemText } from '@/components/molecules/section/Grid';
import Container from '@/components/molecules/section/Container';
import { transformDateString } from '@/utilities/datetime';
import ModalView from '@/components/modals/Modal';
import { useClientConnect, useClientId } from '@/hooks/client';
import { useClientActionTracking } from '@/hooks/item';
import { Label, PersonaKycBadge, ValueText } from '@/components/atoms/Section';
import { useClipboardCopy } from '@/hooks/common';
import ClipboardSnackbar from '@/components/molecules/common/ClipboardSnackbar';
import RemoveVerifiedPhoneModal from '@/components/modals/RemoveVerifiedPhone';

const BasicInfo: React.FC<{ client: Client; isBuyer: boolean; showKycBadge?: boolean }> = ({
  client,
  isBuyer,
  showKycBadge = true,
}) => {
  const { t } = useTranslation();

  const [modal, setModal] = useState<Modal | null>(null);

  const [showDeletePhoneModal, setShowDeletePhoneModal] = useState(false);

  const connectR = useClientConnect(Number(client.clientId));

  const { refetch: refetchClient } = useClientId(Number(client.clientId));

  const clientTracking = useClientActionTracking(Number(client.clientId));

  const { copy, onSnackbarClose, snackbarOpen } = useClipboardCopy();

  useEffect(() => {
    if (connectR.kind === 'Loading') {
      setModal({ kind: 'Loading', title: t('modal.client_connect.title'), onClose: () => setModal(null) });
    }

    if (connectR.kind === 'Error') {
      setModal({
        kind: 'Error',
        title: t('modal.client_connect.title'),
        error: connectR.error,
        onClose: () => setModal(null),
      });
    }

    if (connectR.kind === 'Completed') {
      const data = connectR.data;
      const date = new Date();

      // Set it to 15min
      date.setTime(date.getTime() + 15 * 60 * 1000);
      document.cookie = `${data.cookieName}=${data.sessionId};expires=${date.toUTCString()};domain=${
        data.cookieDomain
      };path=/`;
      document.cookie = `vc_uid=${client.clientId};expires=${date.toUTCString()};domain=${data.cookieDomain};path=/`;

      if (data.redirectTo === undefined || data.redirectTo == null) {
        setModal({
          kind: 'Error',
          title: t('modal.client_connect.title'),
          onClose: () => setModal(null),
        });
      } else {
        window.open(data.redirectTo, '_blank');
        setModal(null);
      }
    }
  }, [connectR.kind]);

  const actions: GridAction[] = isBuyer
    ? [
        {
          kind: 'Button',
          label: t('client.basic_info.actions.user_front_office'),
          onClick: () => {
            connectR.mutate('buyer');
          },
        },
        {
          kind: 'Button',
          label: t('client.basic_info.actions.stats'),
          onClick: () =>
            setModal({
              kind: 'ClientStats',
              clientId: Number(client.clientId),
              onClose: () => setModal(null),
            }),
        },
        {
          kind: 'Button',
          label: t('client.basic_info.actions.action_histo'),
          onClick: () => {
            clientTracking.mutate('view_action_histo');
            setModal({ kind: 'ClientActionHisto', client, onClose: () => setModal(null) });
          },
        },
      ]
    : [
        {
          kind: 'Button',
          label: t('client.basic_info.actions.user_front_office'),
          onClick: () => {
            connectR.mutate('seller');
          },
        },
        {
          kind: 'Button',
          label: t('client.basic_info.actions.badges'),
          onClick: () => setModal({ kind: 'ClientBadges', client, onClose: () => setModal(null) }),
        },
        {
          kind: 'Button',
          label: t('client.basic_info.actions.stats'),
          onClick: () =>
            setModal({
              kind: 'ClientStats',
              clientId: Number(client.clientId),
              onClose: () => setModal(null),
            }),
        },
        {
          kind: 'Button',
          label: t('client.basic_info.actions.action_histo'),
          onClick: () => {
            clientTracking.mutate('view_action_histo');
            setModal({ kind: 'ClientActionHisto', client, onClose: () => setModal(null) });
          },
        },
        {
          kind: 'Button',
          label: t('client.basic_info.actions.sales_reports'),
          onClick: () => {
            setModal({ kind: 'ClientSalesReports', client, onClose: () => setModal(null) });
          },
        },
      ];

  // BE response empty verifiedPhoneNumber as '-'
  const hasPhone = !!client.verifiedPhoneNumber && client.verifiedPhoneNumber != '-' && !client.awaitingPhoneValidation;

  const getAwaitPhoneStatus = () => {
    if (!hasPhone) return '-';
    return `${client.verifiedPhoneNumber} (${t('client.basic_info.verified')})`;
  };

  const items = [
    itemHtml(
      t('client.basic_info.first_last_name'),
      <>
        <ValueText
          label={client.name || '-'}
          kind="Text"
        />
        {showKycBadge && client.personaKycStatus === PersonaKycStatus.APPROVED && <PersonaKycBadge />}
      </>,
    ),
    itemText(
      <Label labelInfo={{ label: t('client.basic_info.login_id'), onCopy: () => copy(client.customerId) }} />,
      client.customerId,
    ),
    itemText(
      t('client.basic_info.cs_score'),
      client.csScore,
      {
        kind: 'Button',
        label: t('client.basic_info.cs_score_history'),
        disabled: !client.csScoreV2History?.length,
        onClick: () => {
          clientTracking.mutate('view_cs_score_history');
          setModal({ kind: 'ClientCsScoreHistory', client, onClose: () => setModal(null) });
        },
      },
      [4, 5].includes(client.csScore || -1) ? '#ff5555' : undefined,
    ),
    itemText(t('client.basic_info.status'), client.clientStatus, undefined, client.customerStatusColor),
    itemText(t('client.basic_info.type_of_profile'), client.customerType),
    itemText(t('client.basic_info.awaiting_phone_verification'), getAwaitPhoneStatus(), {
      kind: 'Button',
      label: t('common.remove').toUpperCase(),
      onClick: () => setShowDeletePhoneModal(true),
      disabled: !hasPhone,
    }),
    itemText(
      t('client.basic_info.active'),
      convertActive(t, client.accountStatus),
      undefined,
      client.accountStatus === 'ghosted' ? '#EB2F06' : '',
    ),
    itemText(t('client.basic_info.country'), client.country),
    itemText(t('client.basic_info.language'), client.language),
    itemText(t('client.basic_info.currency'), client.currency),
    itemText(t('client.basic_info.sales'), client.salesNumber),
    itemText(t('client.basic_info.purchases'), client.purchasesCount),
    itemText(
      t('client.basic_info.newsletter'),
      client.emailSubscribed ? t('client.basic_info.subscribed') : t('client.basic_info.unsubscribed'),
    ),
    itemText(
      t('client.basic_info.newsletter_unsubscribed_date'),
      transformDateString(client.emailUnsubscribedDate, 'DD/MM/YYYY'),
    ),
    itemList(t('client.basic_info.referral_code'), client.referralCodes || null),
  ];

  return (
    <>
      <Container
        title={t('client.basic_info.title')}
        testId="basic_info"
        actions={actions}
      >
        <Grid
          column={5}
          items={items}
        />
      </Container>
      <ModalView modal={modal} />

      {!!client.verifiedPhoneNumber && showDeletePhoneModal && (
        <RemoveVerifiedPhoneModal
          clientId={client.clientId}
          onClose={() => setShowDeletePhoneModal(false)}
          refetchClient={refetchClient}
        />
      )}

      <ClipboardSnackbar
        open={snackbarOpen}
        onClose={onSnackbarClose}
      />
    </>
  );
};

function convertActive(t: TFunction, active: string | null) {
  if (active == null) {
    return null;
  }
  switch (active) {
    case 'active':
      return `✅ ${t('common.active')}`;
    case 'inactive':
      return `❌ ${t('common.inactive')}`;
    default:
      return `👻 ${t('client.basic_info.ghosted')}`;
  }
}

export default BasicInfo;
